import { CheckCircle2, XCircle, AlertTriangle, Clock, Ban, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtQty } from "@/lib/api";

const STATUS = {
  en_attente: ["En attente de validation", "bg-amber-100 text-amber-800", Clock],
  validee: ["Validée", "bg-emerald-100 text-emerald-800", CheckCircle2],
  annulee: ["Annulée", "bg-slate-200 text-slate-700", XCircle],
  bloquee: ["Bloquée", "bg-rose-100 text-rose-800", Ban],
  remplacee: ["Remplacée", "bg-slate-100 text-slate-500", RefreshCw],
};

const TYPE_CLS = {
  entree: "bg-emerald-100 text-emerald-800", sortie: "bg-slate-200 text-slate-800", ajustement: "bg-blue-100 text-blue-800",
  sortie_kit: "bg-purple-100 text-purple-800", create_article: "bg-orange-100 text-orange-800", update_article: "bg-orange-100 text-orange-800",
  create_kit: "bg-orange-100 text-orange-800", update_kit: "bg-orange-100 text-orange-800",
};

function Impacts({ impacts }) {
  return (
    <div className="mt-2 overflow-x-auto rounded border bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500"><tr><th className="px-2 py-1.5 text-left">Composant</th><th className="px-2 py-1.5 text-right">Besoin</th><th className="px-2 py-1.5 text-right">Stock</th><th className="px-2 py-1.5 text-right">Manque</th></tr></thead>
        <tbody>
          {impacts.map((c) => (
            <tr key={c.code} className={c.missing > 0 ? "bg-rose-50" : ""}>
              <td className="px-2 py-1.5"><span className="font-mono font-semibold">{c.code}</span> {c.designation}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtQty(c.need)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtQty(c.before)} → {c.missing > 0 ? "—" : fmtQty(c.after)}</td>
              <td className={`px-2 py-1.5 text-right font-mono font-semibold ${c.missing > 0 ? "text-rose-600" : "text-emerald-600"}`}>{c.missing > 0 ? fmtQty(c.missing) : "✓"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Item({ it, testid }) {
  return (
    <li className={`rounded border p-3 ${it.ok ? "bg-slate-50" : "border-rose-200 bg-rose-50/60"}`} data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${TYPE_CLS[it.type]}`}>{it.label}</span>
        <span className="font-mono font-semibold">{it.code}</span>
        <span className="text-sm text-slate-600">{it.designation}</span>
        {it.quantity != null && !it.type.startsWith("create_kit") && !it.type.startsWith("update") && (
          <span className="ml-auto font-mono text-sm font-bold">{it.type === "ajustement" ? "= " : it.type === "sortie" || it.type === "sortie_kit" ? "− " : "+ "}{fmtQty(it.quantity)}</span>
        )}
      </div>
      {it.before != null && it.after != null && it.type !== "sortie_kit" && (
        <div className="mt-1 font-mono text-xs text-slate-600" data-testid={`${testid}-stock`}>Stock : {fmtQty(it.before)} → <b className="text-slate-900">{fmtQty(it.after)}</b></div>
      )}
      {it.details?.map((d) => <div key={d} className="mt-1 text-xs text-slate-600">{d}</div>)}
      {it.impacts && <Impacts impacts={it.impacts} />}
      {it.errors?.map((e) => <div key={e} className="mt-1.5 flex gap-1.5 text-xs font-medium text-rose-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{e}</div>)}
    </li>
  );
}

export function ProposalCard({ proposal, idx, busy, onConfirm, onCancel }) {
  const [label, cls, Icon] = STATUS[proposal.status] || STATUS.en_attente;
  const pending = proposal.status === "en_attente";
  return (
    <div className="mt-3 rounded-md border-2 border-slate-200 bg-white" data-testid={`proposal-card-${idx}`}>
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="font-display text-lg font-bold uppercase tracking-wide">Proposition</span>
        <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={`proposal-status-${idx}`} data-status={proposal.status}><Icon className="h-3.5 w-3.5" />{label}</span>
      </div>
      <ul className="space-y-2 p-3">{proposal.items.map((it, i) => <Item key={i} it={it} testid={`proposal-item-${idx}-${i}`} />)}</ul>
      {proposal.status === "bloquee" && proposal.issues?.length > 0 && (
        <div className="mx-3 mb-3 rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800" data-testid={`proposal-issues-${idx}`}>
          <b>Validation impossible :</b>
          <ul className="mt-1 list-disc pl-4">{proposal.issues.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      {proposal.result && <div className="mx-3 mb-3 rounded bg-emerald-50 p-2.5 text-xs text-emerald-800" data-testid={`proposal-result-${idx}`}>Appliqué par {proposal.result.by} — {proposal.result.movements} mouvement(s) enregistré(s) (note « via Assistant IA »).</div>}
      {(pending || proposal.status === "bloquee") && (
        <div className="flex justify-end gap-2 border-t p-3">
          <Button variant="outline" className="h-11" disabled={busy} onClick={onCancel} data-testid={`proposal-cancel-${idx}`}>{pending ? "Annuler" : "Fermer"}</Button>
          <Button className="h-11 gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !pending || !proposal.can_confirm} onClick={onConfirm} data-testid={`proposal-confirm-${idx}`}><CheckCircle2 className="h-4 w-4" /> Valider</Button>
        </div>
      )}
    </div>
  );
}
